import { useRef, useLayoutEffect } from 'react'

const FADE_DURATION = '0.35s'
const FADE_EASING = 'ease-in'

export default function useProgressiveReveal (containerRef, isGenerating, content) {
  const revealedCountRef = useRef(0)
  const hasEverGeneratedRef = useRef(false)
  const prevChildNodesRef = useRef([])

  if (isGenerating) {
    hasEverGeneratedRef.current = true
  }

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const children = container.children
    const childCount = children.length

    if (!hasEverGeneratedRef.current) {
      revealedCountRef.current = childCount
      return
    }

    const currentNodes = Array.from(children)
    const prevNodes = prevChildNodesRef.current

    let stablePrefix = 0
    for (let i = 0; i < Math.min(prevNodes.length, currentNodes.length); i++) {
      if (prevNodes[i] === currentNodes[i]) {
        stablePrefix++
      } else {
        break
      }
    }

    if (stablePrefix < revealedCountRef.current) {
      for (let i = 0; i < stablePrefix; i++) {
        currentNodes[i].dataset.revealed = '1'
        currentNodes[i].style.opacity = '1'
      }
      revealedCountRef.current = stablePrefix
    }

    const alreadyRevealed = revealedCountRef.current

    for (let i = alreadyRevealed; i < childCount; i++) {
      const child = children[i]
      if (child.dataset.revealed) continue

      child.style.transition = 'none'
      child.style.opacity = '0'
      // eslint-disable-next-line no-unused-expressions
      child.offsetHeight
      child.style.transition = `opacity ${FADE_DURATION} ${FADE_EASING}`
      child.style.opacity = '1'
      child.dataset.revealed = '1'
    }

    revealedCountRef.current = childCount
    prevChildNodesRef.current = currentNodes
  }, [content, isGenerating, containerRef])

  useLayoutEffect(() => {
    if (!content) {
      revealedCountRef.current = 0
      prevChildNodesRef.current = []
    }
  }, [content])
}
